import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'
import { supabase } from '../supabaseClient'
import { billedOverageCents } from '../utilities/usageAxis'
import type { UsageSeriesRow } from '../utilities/usageSeries'
import { previousPeriod } from '../utilities/usagePeriods'
import { ORG_USAGE_COLUMNS, readsMoney } from '../utilities/moneyReads'
import useOrganizations from './useOrganizations'

// One axis row from get_org_usage. Axes with an allowance: bandwidth | compute | storage | nodes
// (seats are free — no seat axis). included is null on a non-metered (Enterprise) plan ⇒ unlimited.
// The six informational <leg>_receive/<leg>_transmit split rows carry overage_cents 0 and null
// included; the billable legs (relay/storage receive + both proxy legs) sum to the bandwidth axis,
// the rest are free.
//
// ⚠ `unit` is 'bytes' | 'count' | 'cents' and it is NOT decoration: compute's used / included /
// overage are money, not bytes. Render every amount through `formatAxisAmount`, which reads this
// field — never through a formatter chosen by the axis name.
// ⚠ An allowance is not a bill. Only bandwidth and compute can raise overage cents; storage and
// nodes still report an allowance whose overage is a structural zero (router migration 0108).
export type UsageAxis = {
  axis: string
  used: number
  included: number | null
  overage: number | null
  overageCents: number
  unit: string
}

// One row of the compute breakdown (get_org_compute_usage, router#934). app#136 item 3.
//
// ⚠⚠ `class` IS NULLABLE AND A NULL ROW IS UNPRICED, NEVER $0. Tasks provisioned before the class
// catalogue existed carry pow2-clamped shapes that match no class, and `docs/PRICING.md` § 4.2
// requires they render as unpriced rather than snapped to the nearest one — rendering $0.00 tells
// a customer we ran their task for free.
//
// ⚠ `openTasks` is what makes a row STILL CLIMBING: those tasks have not finished, so the hours
// and the cost beside them are a floor and not a total. A reader comparing the number to an
// invoice needs to know which rows are still moving.
//
// ⚠ `allowanceCents` / `allowanceRemainingCents` are the ORG's, repeated on every row by the RPC.
// Read them from any row; they are not per class.
export type ComputeUsage = {
  // ⚠ NULL for a shape the catalogue does not price. Read from the migration, not guessed:
  // `get_org_compute_usage`'s second UNION arm selects `null` for class AND price AND cost.
  class: string | null
  cpu: number
  memoryMb: number
  priceCentsPerHour: number | null
  taskCount: number
  openTasks: number
  billedMinutes: number
  hours: number
  // ⚠⚠ NULL, NOT ZERO, for an off-catalogue shape. A catalogue row is `coalesce(u.cost_cents, 0)`
  // and can genuinely be 0 (an offered class nobody used); an off-catalogue row is a literal
  // `null`. Typing this `number` would make `formatCents(null)` render "$0.00" and tell a
  // customer we ran their task for free — the one thing PRICING § 4.2 forbids.
  costCents: number | null
  // ⚠⚠ BOTH ARE NULL FOR THE EXEMPT TIER. The migration selects
  // `case when v_metered then v_allowance else null end`, so an unmetered (Enterprise) org gets
  // null on every row. Typing these `number` would render "$0.00 included this period" to a
  // customer who is not metered at all.
  allowanceCents: number | null
  allowanceRemainingCents: number | null
}

// Per-network rollup (get_usage_summary) — used for the cost-hotspot drill-down.
// ⚠ storageBytesPeak here is the NETWORK half of the org's storage axis only. Node storage
// belongs to no network (router 0086) and arrives separately as NodeStorage; the two halves sum
// to the org storage ring and neither alone does. See utilities/storage.ts.
export type NetworkUsage = {
  networkId: string
  networkName: string
  networkDeleted: boolean // soft-deleted mid-period: still billed, so still shown (router 0089)
  bandwidthBytes: number // billable = relay/storage receive + both proxy legs (bytes the org is charged for)
  storageBytesPeak: number
  storageTransferBytes: number
  nodeCountPeak: number
  dnsQueries: number
}

// Per-NODE storage peak (get_node_storage_summary, router 0087) — the sibling of the per-network
// rows above and the other half of the org's storage axis. A node's storage follows the MACHINE,
// so it cannot be attributed to one of the node's networks without inventing an answer; it rolls
// straight to the org. Rows cover every org the caller belongs to, so scope by orgId.
export type NodeStorage = {
  orgId: string
  orgName: string
  nodeId: string
  nodeName: string
  nodeDeleted: boolean // deleted mid-period: the peak it reached is still on the invoice
  storageBytesPeak: number
}

// One live grant on the usage ledger (get_org_credit, router migration 0138). The app shows the
// two kinds a customer did not buy and did not expect — a promotion or a service credit — because
// those make THIS period's allowance larger than the plan they bought (app#171).
//
// ⚠⚠ THE RPC CARRIES NO `reason` AND MUST NOT GROW ONE HERE. That column is operator text written
// for staff, and router#1057 is explicit that it is never rendered anywhere a tenant controls.
//
// ⚠ `amount` is the grant as issued and `remaining` is what is left of it. The notice reads
// `remaining`: a spent grant is as gone as an expired one.
export type UsageGrant = {
  grantId: string
  axis: string // bandwidth | compute | money
  kind: string // plan_allowance | promotion | compensation | purchased
  amount: number
  remaining: number
  grantedAt: string
  expiresAt: string | null
}

// Per-node traffic (get_node_traffic), from the node's point of view. Shown for visibility; billing
// aggregates at the org level. The three *Receive legs are bytes INTO the node (billed); the
// three *Transmit legs are out of the node (free). relay = node↔node + control-plane, proxy = the
// node's published services, storage = WebDAV/object download (receive) / upload (transmit).
export type NodeTraffic = {
  nodeId: string
  nodeName: string
  networkId: string
  networkName: string
  relayReceive: number
  proxyReceive: number
  storageReceive: number
  relayTransmit: number
  proxyTransmit: number
  storageTransmit: number
}

// The six directional legs alone (relay/proxy/storage × receive/transmit) — the shape the 2×3
// TrafficMatrix renders. A NodeTraffic already satisfies it; network/org aggregates produce it too.
export type DirectionalTraffic = {
  relayReceive: number
  proxyReceive: number
  storageReceive: number
  relayTransmit: number
  proxyTransmit: number
  storageTransmit: number
}

// Sum the six legs across node rows (the network view aggregates its nodes; the org view could too).
export const sumTraffic = (rows: DirectionalTraffic[]): DirectionalTraffic =>
  rows.reduce(
    (acc, r) => ({
      relayReceive: acc.relayReceive + r.relayReceive,
      proxyReceive: acc.proxyReceive + r.proxyReceive,
      storageReceive: acc.storageReceive + r.storageReceive,
      relayTransmit: acc.relayTransmit + r.relayTransmit,
      proxyTransmit: acc.proxyTransmit + r.proxyTransmit,
      storageTransmit: acc.storageTransmit + r.storageTransmit,
    }),
    { relayReceive: 0, proxyReceive: 0, storageReceive: 0, relayTransmit: 0, proxyTransmit: 0, storageTransmit: 0 }
  )

// The scalar the six legs add up to — the bytes this subject put on the org's bandwidth meter.
// ⚠ ALL SIX, and that is the whole rule since router migration 0042: billing charges the node's
// TOTAL traffic. Relay is single-sided at the metering site (nodes.absorbs_relay_cost), so summing
// receive and transmit counts each relayed byte exactly once and nothing double-counts. This is the
// same number TrafficMatrix prints in its bottom-right cell; the allowance line reads it too, so
// the two can never disagree about what the matrix is worth.
export const totalTraffic = (t: DirectionalTraffic): number =>
  t.relayReceive + t.proxyReceive + t.storageReceive + t.relayTransmit + t.proxyTransmit + t.storageTransmit

// The org view aggregates by OWNER — get_org_usage already returns that, as six informational axes
// (<leg>_<direction>, overage_cents 0). Pull the six legs out for the matrix.
export const orgTrafficFromAxes = (axes: UsageAxis[]): DirectionalTraffic => {
  const used = (axis: string): number => axes.find(a => a.axis === axis)?.used ?? 0
  return {
    relayReceive: used('relay_receive'),
    proxyReceive: used('proxy_receive'),
    storageReceive: used('storage_receive'),
    relayTransmit: used('relay_transmit'),
    proxyTransmit: used('proxy_transmit'),
    storageTransmit: used('storage_transmit'),
  }
}

// Per-service traffic within a network (get_service_usage), from the node's point of view.
// receiveBytes = request in (to the node), transmitBytes = response out (to the internet); a proxy
// service is billed on both legs.
export type ServiceUsage = {
  serviceId: string
  serviceTitle: string
  receiveBytes: number
  transmitBytes: number
  requestCount: number
  errorCount: number
}

// 'YYYY-MM' UTC calendar month — mirrors the server current_period() / PeriodBounds
// (router src/db/billing_period.go) so the app and the invoice agree on the window.
export const currentPeriod = (): string => new Date().toISOString().slice(0, 7)

export const periodBounds = (period: string): { start: string; end: string } => {
  const [year, month] = period.split('-').map(Number)
  return {
    start: new Date(Date.UTC(year, month - 1, 1)).toISOString(),
    end: new Date(Date.UTC(year, month, 1)).toISOString(),
  }
}

// ⚠ The period switcher's months are `recentPeriods` in `utilities/usagePeriods.ts`, beside the
// epoch that floors them — a month before the go-live reset holds no usage and is not offered. It
// lives there rather than here so it can carry a test: `src/**/*.test.ts` runs under plain node,
// which cannot import this store.

// Human label for a period — e.g. 'June 2026' (UTC, matching the period's calendar month).
export const formatPeriod = (period: string): string =>
  new Date(`${period}-01T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })

// 'This month' for the current period, else the month name. The node/network Traffic boxes follow
// the shared usage period (set in /usage), so their caption can't hardcode 'This month'.
export const periodLabel = (period: string): string =>
  period === currentPeriod() ? 'This month' : formatPeriod(period)

// The flat plan base plus the period-to-date overage linearly extrapolated to the full period. An
// ESTIMATE — the billing portal has the authoritative invoice. Past periods (fully elapsed) are
// exact. The base is fixed (not extrapolated); seats are free, so there's no per-seat term.
//
// ⚠ `baseCents` IS THE CALLER'S DECISION, computed at render (app#264). The store holds the
// plan's monthly fee as `baseCents`; `flatBaseCents` in `utilities/subscription.ts` zeroes it for
// an annual subscription and for a past period, and the interval it reads lands in `useBilling`
// AFTER this store has fetched — so a figure computed here would not follow it.
//
// ⚠ WHICH axes are summed is `billedOverageCents`, and that set is a copy of the router's
// `billing.meteredAxes` — see the warning where it is defined (utilities/usageAxis.ts). It used to
// be a local set naming bandwidth, storage and nodes: one live axis and two retired by router
// migration 0108, with compute — the other live one — missing (app#152).
export const projectedBillCents = (axes: UsageAxis[], period: string, baseCents: number, now = Date.now()): number => {
  const overage = billedOverageCents(axes)
  const { start, end } = periodBounds(period)
  const total = new Date(end).getTime() - new Date(start).getTime()
  const elapsed = now - new Date(start).getTime()
  const frac = total <= 0 || elapsed >= total || elapsed <= 0 ? 1 : elapsed / total
  return baseCents + Math.ceil(overage / frac)
}

// The newest measurement each axis includes (get_usage_freshness, router migration 0148), keyed by
// axis. Null means no measurement in the period — an absence, never "fresh".
//
// ⚠⚠ IT COMES FROM THE DATA AND MUST NEVER BE REPLACED BY THE CLOCK. Our figures are sums over
// rollups that lag, so a screen stamped `now()` turns a lag the customer could have understood into
// a number they will trust and act on.
export type UsageFreshness = Record<string, string | null>

export interface UsageState {
  period: string
  loading: boolean
  axes: UsageAxis[]
  networks: NetworkUsage[]
  nodeStorage: NodeStorage[] // per-node storage peaks — the node half of the storage axis
  grants: UsageGrant[] // live ledger grants, for the boost notice (app#171)
  freshness: UsageFreshness // newest measurement per axis (app#170)
  compute: ComputeUsage[] // per-class compute breakdown (app#136 item 3)
  computeError: boolean // the compute breakdown read failed, so `compute` is not an empty month
  usageSeries: UsageSeriesRow[] // per-day usage per axis, for the chart (app#146 item 3)
  previousSeries: UsageSeriesRow[] // the same, for the month before: the chart's grey days
  services: Record<string, ServiceUsage[]> // by networkId, fetched lazily on drill-in
  nodeTraffic: Record<string, NodeTraffic | null> // by nodeId, fetched lazily on the node view
  nodeTrafficAll: NodeTraffic[] | null // every org node's traffic, fetched lazily for network aggregates
  planName: string | null
  baseCents: number // the plan's flat MONTHLY fee; `flatBaseCents` decides whether the period owes it
  set: (state: Partial<UsageState>) => void
  get: () => Promise<void>
  loadServices: (networkId: string) => Promise<void>
  loadNodeTraffic: (nodeId: string) => Promise<void>
  loadAllTraffic: () => Promise<void>
  setPeriod: (period: string) => void
  reset: () => void
}

const state = {
  period: currentPeriod(),
  loading: false,
  axes: [] as UsageAxis[],
  networks: [] as NetworkUsage[],
  nodeStorage: [] as NodeStorage[],
  grants: [] as UsageGrant[],
  freshness: {} as UsageFreshness,
  compute: [] as ComputeUsage[],
  computeError: false,
  usageSeries: [] as UsageSeriesRow[],
  previousSeries: [] as UsageSeriesRow[],
  services: {} as Record<string, ServiceUsage[]>,
  nodeTraffic: {} as Record<string, NodeTraffic | null>,
  nodeTrafficAll: null as NodeTraffic[] | null,
  planName: null as string | null,
  baseCents: 0,
}

// Usage and Billing both call get() on mount/org-switch with identical params — without
// dedupe every dashboard open ran the four month-scan RPCs twice. One in-flight promise is
// shared per (org, period), and a successful result is considered fresh for a short window
// (month-to-date numbers can't move meaningfully in 15 s). Errors don't mark freshness, so
// a failed load retries on the next call.
const FRESH_MS = 15_000
let inflight: { key: string; promise: Promise<void> } | null = null
let fetchedAt: { key: string; at: number } | null = null

// The month before, for the over-time chart's grey days. It is read APART from `fetchUsage`'s
// other reads and kept, by `${orgId}:${period of the month before}`.
//
// ⚠⚠ APART, because `get()` has callers that draw no chart (`Node`, `Network`,
// `ModalComputeNode`), and `fetchUsage` sets nothing until every read in its `Promise.all` has an
// answer. Early in a month this read is a full month where the others are one or two days. Inside
// the `Promise.all` it made each ring, bar and traffic figure wait for a chart's grey bars.
//
// ⚠ KEPT, because a closed month changes only while its last day is rolled up. One hour is long
// against that and short against a tab that stays open.
//
// ⚠⚠ `previousWanted` IS WHAT STOPS ANOTHER ORG'S DAYS. An answer is written to the store only
// while its key is the one the latest `fetchUsage` asked for, and `fetchUsage` itself writes the
// kept rows for ITS key or an empty array. So the store never holds the month before of an org or
// a period that is not the one on screen.
const PREVIOUS_FRESH_MS = 3_600_000
let previousHeld: { key: string; at: number; rows: UsageSeriesRow[] } | null = null
let previousWanted: string | null = null

const heldPrevious = (key: string | null): UsageSeriesRow[] | null =>
  key !== null && previousHeld?.key === key && Date.now() - previousHeld.at < PREVIOUS_FRESH_MS
    ? previousHeld.rows
    : null

async function fetchPrevious(
  orgId: string,
  priorPeriod: string,
  key: string,
  set: (s: Partial<UsageState>) => void
): Promise<void> {
  const before = periodBounds(priorPeriod)
  const { data, error } = await supabase.rpc('get_org_usage_series', {
    p_org_id: orgId,
    p_start: before.start,
    p_end: before.end,
  })
  // ⚠ A failed read is not kept: the chart draws the period alone, and the next `get()` asks again.
  if (error || !data) return
  const rows = data as UsageSeriesRow[]
  previousHeld = { key, at: Date.now(), rows }
  if (previousWanted === key) set({ previousSeries: rows })
}

async function fetchUsage(
  orgId: string,
  period: string,
  key: string,
  set: (s: Partial<UsageState>) => void
): Promise<void> {
  const { start, end } = periodBounds(period)
  // The month before, whose last days the chart draws in grey before the period. ⚠ Only for the
  // current period: a past period holds all its days and the chart draws it alone. Nor for a month
  // below the epoch, which holds no usage (`previousPeriod`).
  const priorPeriod = period === currentPeriod() ? previousPeriod(period) : null
  const priorKey = priorPeriod ? `${orgId}:${priorPeriod}` : null
  previousWanted = priorKey
  // ⚠ Not awaited — see `previousHeld`. A rejection is a failed read: the chart draws the period alone.
  if (priorPeriod && priorKey && heldPrevious(priorKey) === null) {
    fetchPrevious(orgId, priorPeriod, priorKey, set).catch(error => console.warn(error))
  }
  set({ loading: true })
  try {
    const [usageRes, summaryRes, nodeStorageRes, orgNetworkRes, orgRes, grantsRes, freshRes, computeRes, seriesRes] =
      await Promise.all([
      supabase.rpc('get_org_usage', { p_org_id: orgId, p_start: start, p_end: end }),
      supabase.rpc('get_usage_summary', { p_start: start, p_end: end }),
      supabase.rpc('get_node_storage_summary', { p_start: start, p_end: end }),
      supabase.from('networks').select('id').eq('org_id', orgId),
      // ⚠⚠ NO `spend_cap_cents` (app#376): router migration 0222 withholds it, and a select that
      // names it fails as a whole, which would take the plan name with it. Nothing on the usage
      // page read it; the Owner's cap comes from `credit` op=status (useBilling).
      supabase.from('organizations').select(ORG_USAGE_COLUMNS).eq('id', orgId).single(),
      // ⚠ NOT period-scoped, because the function is not: it answers "what does this org hold
      // NOW". `liveBoosts` is what keeps it off a past month — see utilities/grants.ts.
      // ⚠⚠ OWNER ONLY since router migration 0222 (the grants are the credit balance, § 5 Org):
      // an Admin or a Member gets 42501, so they do not ask, and the boost notice has no rows.
      readsMoney(useOrganizations.getState().role)
        ? supabase.rpc('get_org_credit', { p_org_id: orgId })
        : Promise.resolve({ data: [] as UsageGrant[], error: null }),
      supabase.rpc('get_usage_freshness', { p_org_id: orgId, p_start: start, p_end: end }),
      // app#136 item 3. Period-scoped, unlike get_org_credit above.
      supabase.rpc('get_org_compute_usage', { p_org_id: orgId, p_start: start, p_end: end }),
      // app#146 item 3 / router#1165. The same window as get_org_usage, with a DATE on it: the
      // days sum to the period, so the chart's total and the ring above it cannot disagree.
      supabase.rpc('get_org_usage_series', { p_org_id: orgId, p_start: start, p_end: end }),
    ])

    const axes = (usageRes.data ?? []).map(r => camelcaseKeys(r)) as UsageAxis[]
    // get_usage_summary is scoped to ALL the caller's orgs (user_org_ids), not the active
    // one — narrow it to the active org's networks (UX scoping, matching every resource store).
    //
    // ⚠ The id set is read from the table, INCLUDING tombstones, and deliberately NOT from
    // useNetwork: that store holds live networks only (its get() says `.is('deleted_at', null)`),
    // so filtering through it dropped every network deleted mid-period — the same rows router
    // migration 0089 put back into the RPC precisely because get_org_usage bills them. Dropping
    // them here is what made the drill-down smaller than the ring above it. The SELECT policy on
    // networks carries no deleted_at predicate, so the tombstones come back; if that ever changes
    // the filter narrows again and the reconciliation line below reports the difference instead
    // of hiding it.
    const orgNetworkIds = new Set((orgNetworkRes.data ?? []).map(n => n.id))
    const networks = (summaryRes.data ?? [])
      .map(r => camelcaseKeys(r))
      .filter((n: NetworkUsage) => orgNetworkIds.has(n.networkId)) as NetworkUsage[]
    // The node half of the storage axis. Scoped on the row's OWN org_id (nodes.owner_org_id, the
    // column get_org_usage bills against) rather than on useNode's list — a node deleted
    // mid-period keeps the peak it reached on the invoice and is absent from that store.
    const nodeStorage = (nodeStorageRes.data ?? [])
      .map(r => camelcaseKeys(r))
      .filter((n: NodeStorage) => n.orgId === orgId) as NodeStorage[]
    // PostgREST may embed a to-one relation as an object or a single-element array; handle both
    // so a paid org isn't silently mislabelled "Free". Only the header copy reads `planName` now —
    // the free/paid BEHAVIOUR keys on the plan's hard_caps flag via useBilling's isFreePlan, so
    // that this name and the banner can no longer disagree (app#127).
    type PlanEmbed = { name: string; monthly_fee_cents: number }
    const plansEmbed = orgRes.data?.plans as PlanEmbed | PlanEmbed[] | null | undefined
    const plan = Array.isArray(plansEmbed) ? plansEmbed[0] : plansEmbed
    const planName = plan?.name ?? null
    const baseCents = plan?.monthly_fee_cents ?? 0

    // ⚠ An org with no grants, and an org on a deployment where the ledger is switched off, both
    // return no rows. That is the same empty array and the same absent notice, which is right:
    // there is nothing to tell the customer in either case.
    const grants = (grantsRes.data ?? []).map(r => camelcaseKeys(r)) as UsageGrant[]

    // ⚠ An empty object is the honest answer when the read fails or the router predates 0148:
    // every consumer then renders no stamp, which is what it did before the stamp existed.
    const freshness: UsageFreshness = {}
    for (const row of freshRes.data ?? []) freshness[row.axis] = row.as_of

    // ⚠⚠ A FAILED READ IS NOT AN EMPTY MONTH. The RPC returns one row per offered class even at
    // zero usage, so `[]` from an error would render "$0.00 used · No compute ran this month" under
    // a bar that reads $2.40. `computeError` lets the Compute card render nothing instead.
    const computeError = !!computeRes.error
    const compute = (computeRes.data ?? []).map(r => camelcaseKeys(r)) as ComputeUsage[]

    // ⚠ NOT camelcased, deliberately: every column of this one is already a single word, and
    // running it through camelcaseKeys would buy nothing while making the row type disagree with
    // the RPC's own column names for the next reader who compares them.
    //
    // ⚠ An empty array is the honest answer for a router whose migration 0164 has not been
    // applied: the chart renders nothing and every other number on the page is unaffected.
    const usageSeries = (seriesRes.data ?? []) as UsageSeriesRow[]
    // ⚠ The rows kept for THIS org and month, or none: never what the store held before, which can
    // be another org's. `fetchPrevious` writes them when its answer comes after this one.
    const previousSeries = heldPrevious(priorKey) ?? []

    set({
      axes,
      networks,
      nodeStorage,
      grants,
      freshness,
      compute,
      computeError,
      usageSeries,
      previousSeries,
      planName,
      baseCents,
      loading: false,
    })
    fetchedAt = { key, at: Date.now() }
  } catch (error) {
    console.warn(error)
    set({ loading: false })
  }
}

// Usage is loaded on demand (when the dashboard opens), not in controller.ts — like
// useOrgMembers, it's an active-org-scoped read nobody pays for until they look at it.
// All reads go straight through Supabase (RLS-scoped); no router round-trip (PLAN_2.6).
const useUsage = create<UsageState>()((set, get) => ({
  ...state,
  set,
  get: async () => {
    const orgId = useOrganizations.getState().activeOrgId
    if (!orgId) return
    const period = get().period
    const key = `${orgId}:${period}`
    if (inflight?.key === key) return inflight.promise
    if (fetchedAt?.key === key && Date.now() - fetchedAt.at < FRESH_MS) return
    const promise = fetchUsage(orgId, period, key, set)
    inflight = { key, promise }
    try {
      await promise
    } finally {
      if (inflight?.promise === promise) inflight = null
    }
  },
  loadServices: async networkId => {
    if (get().services[networkId]) return // already drilled in
    const { start, end } = periodBounds(get().period)
    try {
      const { data } = await supabase.rpc('get_service_usage', {
        p_network_id: networkId,
        p_start: start,
        p_end: end,
      })
      const services = (data ?? []).map(r => camelcaseKeys(r)) as ServiceUsage[]
      set({ services: { ...get().services, [networkId]: services } })
    } catch (error) {
      console.warn(error)
    }
  },
  // Per-node traffic for the node view — scoped to a single node (router get_node_traffic's
  // p_node_id), so opening a node fetches just that node instead of every node in the org. Cached
  // by nodeId for the period; cleared on period switch / org reset like the service cache above.
  loadNodeTraffic: async nodeId => {
    if (get().nodeTraffic[nodeId] !== undefined) return // already fetched this period
    const orgId = useOrganizations.getState().activeOrgId
    if (!orgId) return
    const { start, end } = periodBounds(get().period)
    try {
      const { data, error } = await supabase.rpc('get_node_traffic', {
        p_org_id: orgId,
        p_start: start,
        p_end: end,
        p_node_id: nodeId,
      })
      // .rpc() resolves (doesn't throw) on a PostgREST/Postgres error, so check it explicitly:
      // leave [nodeId] undefined rather than caching null, so a later open retries (a transient
      // failure, or the deploy-skew window before router#283's migration lands, mustn't hide the
      // Traffic box for the rest of the period). null stays reserved for "fetched, genuinely empty".
      if (error) {
        console.warn(error)
        return
      }
      const row = (data ?? []).map(r => camelcaseKeys(r))[0] as NodeTraffic | undefined
      set({ nodeTraffic: { ...get().nodeTraffic, [nodeId]: row ?? null } })
    } catch (error) {
      console.warn(error)
    }
  },
  // Every org node's traffic for the period — feeds the network aggregate matrix (sum the rows for a
  // network's nodes). get_node_traffic with no p_node_id returns the whole org; cached per period.
  loadAllTraffic: async () => {
    if (get().nodeTrafficAll !== null) return // already fetched this period
    const orgId = useOrganizations.getState().activeOrgId
    if (!orgId) return
    const { start, end } = periodBounds(get().period)
    try {
      const { data, error } = await supabase.rpc('get_node_traffic', { p_org_id: orgId, p_start: start, p_end: end })
      if (error) {
        console.warn(error)
        return
      }
      set({ nodeTrafficAll: (data ?? []).map(r => camelcaseKeys(r)) as NodeTraffic[] })
    } catch (error) {
      console.warn(error)
    }
  },
  setPeriod: period => {
    // Switching period invalidates the lazy service + node caches; re-fetch the headline + drill-downs.
    set({ period, services: {}, nodeTraffic: {}, nodeTrafficAll: null })
    void get().get()
  },
  reset: () => {
    inflight = null
    fetchedAt = null
    previousHeld = null
    previousWanted = null
    set({ ...state, period: currentPeriod() })
  },
}))

export default useUsage
